import { CurrencyType } from 'api/types/entity/priceEntity';
import { formatPrice, parsePrice } from 'helpers';
import { Autocomplete, NumberInput, Stack } from 'libs/ui';
import { NumberInputProps } from 'libs/ui/NumberInput/types';

import { FieldType } from '../types';
import { useField } from '../useField';

import { CurrencyContainer } from './PriceAndCurrencyField.styles';

export interface TextFieldProps
  extends FieldType,
    Omit<NumberInputProps, 'name' | 'onChange' | 'onChangeText' | 'value'> {}

export interface PriceFieldValue {
  price: number;
  currency: CurrencyType;
}

/**
 * Price field.
 */
export const PriceAndCurrencyField = ({
  name,
  allowSubmit,
  parse = parsePrice,
  format = formatPrice,
  ...props
}: TextFieldProps) => {
  const priceName = `${name}.price`;
  const currencyName = `${name}.currency`;

  const { field, helpers, inputProps } = useField<number | undefined>(
    priceName
  );

  const {
    field: fieldCurrency,
    helpers: helpersCurrency,
    inputProps: inputPropsCurrency,
  } = useField<CurrencyType | undefined>(currencyName);

  const isError = inputProps.error || inputPropsCurrency.error;
  const helperText = inputProps.helperText || inputPropsCurrency.helperText;

  return (
    <Stack direction="row" spacing={16}>
      <NumberInput
        {...props}
        fullWidth
        error={isError}
        format={format}
        helperText={helperText}
        name={name}
        parse={parse}
        readOnly={inputProps.readOnly}
        showCopyButton={inputProps.showCopyButton}
        value={field.value}
        onBlur={field.onBlur(name)}
        onChange={() => {
          if (isError) {
            helpersCurrency.setError(undefined);
            helpers.setError(undefined);
          }
        }}
        onChangeText={(v) => {
          helpers.setValue(v);
        }}
      />
      <CurrencyContainer readOnly={inputProps.readOnly}>
        <Autocomplete
          hideClearIcon
          className="priceField-Currency"
          error={isError}
          fullWidth={false}
          // this is necessary for the label to take up space but not be displayed
          label={`\u00A0`}
          name={currencyName}
          optionLabelKey="label"
          options={Object.values(CurrencyType).map((v) => ({
            value: v,
            label: v,
          }))}
          optionValueKey="value"
          readOnly={inputProps.readOnly}
          showCopyButton={inputProps.showCopyButton}
          value={
            fieldCurrency.value
              ? { value: fieldCurrency.value, label: fieldCurrency.value }
              : null
          }
          variant="select"
          onChange={(e, v) => {
            if (isError) {
              helpersCurrency.setError(undefined);
              helpers.setError(undefined);
            }
            helpersCurrency.setValue(v?.value);
          }}
        />
      </CurrencyContainer>
    </Stack>
  );
};
